import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// Landing copy is fully i18n-driven via t() after the v12 rebuild (plain title + em spans).
import { useReadyTranslation } from '../hooks/useReadyTranslation';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  ArrowRight, ArrowDown, ChevronDown,
  BarChart3, Stethoscope, GraduationCap, Award,
  Menu, X as XIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import LanguageSelector from '../components/LanguageSelector';
import AppStoreBloque from '../components/AppStoreBloque';
import { changeAppLanguage } from '../lib/language';
import { getLegalLinks } from '../lib/legalLinks';
import { IS_APP_BUILD } from '../lib/buildTarget';
import { esRutaEnEspanol, gemelaDeIdioma, rutaLimpia } from '../lib/gemelaDeIdioma';
// La pieza publica (post-t7-2a, bloque A): la barra, las preguntas frecuentes y el pie que
// la portada comparte con las dos paginas de profesores. Una sola fuente, acotada a `.lp`;
// cada una de las tres partes va aqui dentro de su propia caja `.lp` (ver `lv-pieza` en
// index.css). La guarda es scripts/pieza_publica.test.js.
import '../styles/publicas.css';
// La portada rehecha (post-t7-2a, bloque E): su hoja propia (las secciones con la piel de la
// pieza y los dibujos en vivo, acotada a `.lp` y generada desde la maqueta), las escenas de los
// dibujos y los trazos de la curva del olvido.
import '../styles/portada.css';
import { montarEscenas, PALOMA, TACHE } from '../lib/escenasPortada';
import { CURVA } from '../lib/curvaDelOlvido';
import { useAvisoMarca, LLAVE_TEXTO as AVISO_MARCA_TEXTO, LLAVE_CERRAR as AVISO_MARCA_CERRAR } from '../lib/avisoMarca';

// Client-only environment probes. IS_REACT_SNAP is true only inside the prerender
// browser (scripts/prerender.js sets a UA containing "ReactSnap" on purpose to keep
// this guard working; react-snap itself was retired), where we render a clean/static
// state instead of running motion.
const IS_REACT_SNAP = typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent || '');
function prefersReducedMotion() {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
// Los dibujos se mueven solo con JavaScript, fuera del pre-pintado y sin "reducir movimiento".
// Si no, se quedan en el estado final que ya trae el HTML (todo su texto a la vista).
function sePuedeMover() {
  return !IS_REACT_SNAP && !prefersReducedMotion()
    && typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function';
}

const OPT_KEYS = ['A', 'B', 'C', 'D'];

// ---- small inline SVGs sampled 1:1 from the mockup ----
const SVG = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': 'true' };
const TargetGlyph = () => (
  <svg {...SVG} width="20" height="20"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" /></svg>
);
const XGlyph = () => (
  <svg {...SVG} width="20" height="20" style={{ strokeWidth: 3 }}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
);
const PainXGlyph = () => (
  <svg {...SVG} width="20" height="20" style={{ strokeWidth: 2.5 }}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
);
const PalomaGlyph = () => (<svg {...SVG} width="20" height="20"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>);
const TacheGlyph = () => (<svg {...SVG} width="20" height="20"><path d="M6 6l12 12M18 6L6 18" /></svg>);
const FlechaGlyph = () => (<svg {...SVG} width="20" height="20"><path d="M5 12h14M13 6l6 6-6 6" /></svg>);
const SubeGlyph = () => (<svg {...SVG} width="20" height="20"><path d="M12 19V5M6 11l6-6 6 6" /></svg>);
const BajaGlyph = () => (<svg {...SVG} width="20" height="20"><path d="M12 5v14M6 13l6 6 6-6" /></svg>);
const SubirGlyph = () => (
  <svg {...SVG} width="18" height="18"><path d="M12 16V4M7 9l5-5 5 5" /><path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" /></svg>
);
const DificultadGlyph = () => (
  <svg {...SVG} width="20" height="20"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4.5" /><path d="M12 12h.01" /></svg>
);

// ---- HERO: la tarjeta de la pregunta (escena "hq") ----
// El HTML trae el estado FINAL: se eligio B (equivocada), C es la correcta y el porque esta abierto.
// La escena (lib/escenasPortada.js) lo pone en su estado inicial y lo vuelve a contestar en bucle
// mientras la tarjeta esta a la vista: el puntero cruza A, llega a B, la elige y sale el porque.
const FIN_DE_OPCION = ['dim', 'wrong', 'right', 'dim'];
function TarjetaDeArriba({ card }) {
  if (!card || !Array.isArray(card.options)) return null;
  return (
    <div className="lv-pcard pc" data-esc="hq" data-testid="hero-question-card">
      <div className="lv-pcard-head">
        <span className="lv-diff-badge"><TargetGlyph />{card.difficulty}</span>
        <span className="lv-topic-label">{card.topic}</span>
      </div>
      <p className="lv-qtext">{card.question}</p>
      <div className="lv-opts">
        {card.options.map((opt, idx) => (
          <div key={idx} className={`lv-opt ${FIN_DE_OPCION[idx] || 'dim'}`} data-fin={FIN_DE_OPCION[idx] || 'dim'}>
            <span className="lv-k">{OPT_KEYS[idx]}</span><span>{opt}</span>
          </div>
        ))}
      </div>
      <div className="lv-explain show">
        <span className="lv-lbl"><span className="lv-ic"><XGlyph /></span>{card.feedback_label}</span>
        <div>{card.feedback_body}</div>
      </div>
    </div>
  );
}

// ---- la formula de una respuesta (c1, diseno 8.3) ----
// El texto se pinta tal cual; la formula (lo que va despues del ultimo espacio) va en su propia
// caja en linea, para que se quede entera, y solo se puede partir justo despues del signo de igual.
function ConFormula({ texto }) {
  const s = String(texto || '');
  const i = s.lastIndexOf(' ');
  const formula = s.slice(i + 1);
  const igual = formula.indexOf('=');
  if (i < 0 || igual < 0) return <span className="mq-tx">{s}</span>;
  return (
    <span className="mq-tx">
      {s.slice(0, i + 1)}
      <span className="fx">{formula.slice(0, igual + 1)}<wbr />{formula.slice(igual + 1)}</span>
    </span>
  );
}

// ---- los datos de los dibujos (de ejemplo: ninguno es un numero del motor) ----
// "Mas practica donde mas te cuesta": la proporcion con que sale cada tema en una sesion de ejemplo.
const FRECUENCIAS = [['bad', 'struggle', 100], ['off', 'unpracticed', 60], ['warn', 'half', 30], ['ok', 'mastered', 10]];
// "Sube la dificultad": seis preguntas seguidas de un tema; su resultado, su escalon y su insignia.
const SEGUIDAS = [
  { r: 'ok', n: 1, d: 'e' }, { r: 'ok', n: 1, d: 'e' }, { r: 'ok', n: 2, d: 'm' },
  { r: 'ok', n: 2, d: 'm' }, { r: 'ko', n: 3, d: 'h' }, { r: 'now', n: 2, d: 'm' },
];
const INSIGNIA = { e: 'easy', m: 'medium', h: 'hard' };
// "Mezcla los temas": seis preguntas, con su tema, como va el alumno en el y por que sale ahora.
const MEZCLA = [
  { e: 'r', t: 1, r: 'goes_first' }, { e: 'a', t: 2, r: 'switches' }, { e: 'r', t: 1, r: 'back_soon' },
  { e: 'v', t: 3, r: 'review' }, { e: 'r', t: 1, r: 'back_soon' }, { e: 'a', t: 2, r: 'its_turn' },
];
const ESTADO = { r: 'struggling', a: 'halfway', v: 'mastered' };
const TEMA = { 1: 'annuities', 2: 'compound', 3: 'perpetuities' };
// "Como funciona", paso 3: el puntaje que sube sesion tras sesion (de ejemplo).
const SESIONES = [48, 63, 78];
// "Como funciona", paso 2: lo que StudyBuddy saco de ese PDF (48 preguntas y 81 flashcards, reales).
const CUENTAS = { preguntas: 48, flashcards: 81 };

// ---- el texto de una tarjeta con dibujo (titulo y parrafo) y el de un paso de "Como funciona" ----
function TextoDeTarjeta({ titulo, texto }) {
  return <div className="tx"><h3>{titulo}</h3><p>{texto}</p></div>;
}
function PasoDeComo({ paso }) {
  const p = paso || {};
  return <div className="tx"><span className="lv-step-n">{p.number}</span><h3>{p.title}</h3><p>{p.desc}</p></div>;
}

const UC_VISUALS = [
  { icon: Stethoscope, tono: 'o' },
  { icon: GraduationCap, tono: 'b' },
  { icon: Award, tono: 'g' },
];

// Parse an i18n string with <1>...</1> into { prefix, em } for word-by-word GSAP animation.
function splitTransLine(str) {
  if (!str) return { prefix: '', em: '' };
  const m = str.match(/^([\s\S]*?)<1>([\s\S]+?)<\/1>([\s\S]*)$/);
  if (!m) return { prefix: str, em: '' };
  return { prefix: m[1].trim(), em: m[2].trim() };
}

// ---- LA BARRA Y EL PIE DE LA PIEZA PUBLICA, UNA SOLA FUENTE EN REACT ----
// Remate post-t7-2a (R5, firma 3 del 13-oct): la portada y la pagina publica de precios
// (`/pricing` y `/es/precios`, solo en la WEB) pintan la MISMA barra (con su menu de tres rayas)
// y el MISMO pie, desde estas funciones. Viven en ESTE archivo, y no en components/, a proposito:
// las guardas de la portada (footerBlogApp.438, footerBlog.438, footerProfesores.pt72a) leen el
// marcado del pie en LandingPage.js, y asi siguen leyendo la unica copia, sin duplicar nada.
// La portada pinta exactamente el HTML de antes (comparado byte a byte en el pre-pintado).

// El estado de la barra: bajada (`landing-nav-scrolled` pasados los 20 px) y el menu abierto.
export function useEstadoDeLaBarra() {
  const [navScrolled, setNavScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Sticky nav scroll detection
  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return { navScrolled, mobileMenuOpen, setMobileMenuOpen };
}

// Single central mechanism (lib/language.js): applies via i18n.changeLanguage and
// persists (localStorage always; backend for logged-in users). Its no-op guard
// covers the old `(lang === 'es') === isES` early return.
// post-t7-2a (bloque F, diseno 3.4 f): en la web, ademas, NAVEGA a la gemela del idioma
// elegido (de `/` a `/es`, de `/pricing` a `/es/precios` y de vuelta; lib/gemelaDeIdioma.js),
// DESPUES de cambiar el idioma, para que la gemela ya monte en su idioma. En `/` viendose en
// espanol por el navegador, elegir EN se queda en `/`. Solo un toque navega: nadie se redirige
// por idioma.
export function useIdiomaConGemela() {
  const navigate = useNavigate();
  const { user } = useAuth();
  return async (lang) => {
    await changeAppLanguage(lang, user);
    if (IS_APP_BUILD) return;
    const aqui = window.location.pathname;
    const destino = gemelaDeIdioma(aqui, lang);
    if (destino && destino !== rutaLimpia(aqui)) navigate(destino);
  };
}

// La barra y, fuera de su caja, el menu de tres rayas. Con `irA` (la portada) las ligas del
// centro son anclas de la misma pagina que bajan con desplazamiento suave; sin `irA` (precios)
// son ligas normales a la seccion de la portada de SU direccion (`/#faq` o `/es#faq`).
export function BarraPublica({ t, idioma, isLoggedIn, alCambiarIdioma, navScrolled, mobileMenuOpen, setMobileMenuOpen, irA }) {
  const isES = idioma?.startsWith('es');
  const switchLang = alCambiarIdioma;
  const scrollTo = irA;
  const portadaDeAqui = esRutaEnEspanol(window.location.pathname) ? '/es' : '/';
  const navLinks = [
    { label: t('landing:nav.how_it_works'), id: 'how-it-works' },
    { label: t('landing:nav.features'), id: 'features' },
    { label: t('landing:nav.pricing'), id: 'pricing' },
    { label: t('landing:nav.faq'), id: 'faq' },
  ];
  return (
    <>
      {/* ─── NAV ─── */}
      {/* La barra es la de la pieza publica (src/styles/publicas.css), con el mismo marcado
          que la maqueta. Va fija arriba (lv-barra, index.css) y conserva su estado al bajar. */}
      <div className="lp lv-pieza">
        <header className={`p-nav lv-barra${navScrolled ? ' landing-nav-scrolled' : ''}`} data-testid="landing-nav">
          <div className="p-nav-in">
            {/* post-t7-2a (H3): en la direccion en espanol (/es) el logo lleva a /es, como en las
                estaticas en espanol; en / sigue a /. Se lee la direccion, no el idioma. */}
            <Link to={portadaDeAqui} className="p-logo">Quiz<span>essa</span></Link>
            <nav className="p-links">
              {navLinks.map((link) => (scrollTo ? (
                <a key={link.id} href={`#${link.id}`}
                  onClick={(e) => { e.preventDefault(); scrollTo(link.id); }}
                  data-testid={`nav-${link.id}-link`}>
                  {link.label}
                </a>
              ) : (
                <a key={link.id} href={`${portadaDeAqui}#${link.id}`} data-testid={`nav-${link.id}-link`}>
                  {link.label}
                </a>
              )))}
            </nav>
            <div className="p-nav-r">
              <div className="p-lang" data-testid="language-selector-compact">
                <button type="button" onClick={() => switchLang('en')} className={!isES ? 'on' : undefined}
                  data-testid="lang-en-btn-compact">EN</button>
                <span aria-hidden="true">|</span>
                <button type="button" onClick={() => switchLang('es')} className={isES ? 'on' : undefined}
                  data-testid="lang-es-btn-compact">ES</button>
              </div>
              {isLoggedIn ? (
                <Link to="/dashboard" className="p-signup" data-testid="nav-dashboard-btn">
                  {t('common:nav.dashboard')}
                </Link>
              ) : (
                <>
                  <Link to="/login" className="p-login" data-testid="nav-login-btn">{t('landing:nav.login')}</Link>
                  <Link to="/signup" className="p-signup" data-testid="nav-signup-btn">{t('landing:nav.signup')}</Link>
                </>
              )}
              <button type="button" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-burger" aria-expanded={mobileMenuOpen} data-testid="mobile-menu-toggle">
                {mobileMenuOpen ? <XIcon className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </header>
      </div>

      {/* Mobile menu dropdown: vive mientras se ve el boton del menu (abajo de 1104 desde post-t7-2c; lv-menu, index.css) */}
      {mobileMenuOpen && (
        <div className="lv-menu fixed left-0 right-0 z-40 bg-[#0f0e0e]/95 backdrop-blur-xl border-b border-[#262626] px-6 py-4" data-testid="mobile-menu">
          <div className="flex flex-col gap-3">
            {navLinks.map((link) => (scrollTo ? (
              <button key={link.id} onClick={() => scrollTo(link.id)}
                className="text-sm text-[#8c8c8c] hover:text-white transition-colors text-left py-1.5" data-testid={`mobile-nav-${link.id}`}>
                {link.label}
              </button>
            ) : (
              <a key={link.id} href={`${portadaDeAqui}#${link.id}`}
                className="text-sm text-[#8c8c8c] hover:text-white transition-colors text-left py-1.5" data-testid={`mobile-nav-${link.id}`}>
                {link.label}
              </a>
            )))}
            <LanguageSelector variant="mobile-menu" alElegir={switchLang} />
            {!isLoggedIn && (
              <>
                <div className="border-t border-[#262626] my-1" />
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="text-[#8c8c8c] hover:text-white text-sm w-full justify-start h-9 px-0">{t('landing:nav.login')}</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// El pie: las cuatro columnas, el ano y los dos botones de idioma. Sus ligas se deciden por el
// IDIOMA (la misma pieza en la portada y en precios).
export function PiePublico({ t, idioma, alCambiarIdioma }) {
  const isES = idioma?.startsWith('es');
  const switchLang = alCambiarIdioma;
  const vsBase = isES ? '/es/vs' : '/vs';
  const legalLinks = getLegalLinks(idioma);
  const footerLabels = isES
    ? { compare: 'Comparar Quizessa', medical: 'Para Médicos', tools: 'Herramientas', company: 'Empresa', pricing: 'Precios', contact: 'Contacto', professors: 'Para profesores y universidades' }
    : { compare: 'Compare Quizessa', medical: 'Medical Students', tools: 'Study Tools', company: 'Company', pricing: 'Pricing', contact: 'Contact', professors: 'For professors and universities' };
  // post-t7-2a (diseno 4.3): la liga a la pagina de profesores de ESE idioma.
  const professorsHref = isES ? '/es/profesores' : '/professors';
  // post-t7-2a (bloque F, diseno 3.4 g): en espanol "Precios" va a la pagina de precios en
  // espanol; en ingles sigue bajando a la seccion de precios de la portada.
  const pricingHref = isES ? '/es/precios' : '/#pricing';
  // blog-es (decision de Carlos del 30-oct): con los articulos traducidos, el Blog del pie va al
  // blog de ese idioma, como sus vecinos.
  const blogHref = isES ? '/blog/es' : '/blog';
  const medicalLinks = isES
    ? [
        { href: '/es/usmle-ia', label: 'USMLE con IA', testId: 'footer-usmle-ia-link' },
        { href: '/es/mcat-ia', label: 'MCAT con IA', testId: 'footer-mcat-ia-link' },
        { href: '/es/enarm-ia', label: 'ENARM con IA', testId: 'footer-enarm-ia-link' },
        { href: '/es/mir-ia', label: 'MIR con IA', testId: 'footer-mir-ia-link' },
        { href: '/es/eunacom-ia', label: 'EUNACOM (Chile)', testId: 'footer-eunacom-link' },
      ]
    : [
        { href: '/usmle-flashcards', label: 'USMLE Flashcards', testId: 'footer-usmle-flashcards-link' },
        { href: '/mcat-flashcards', label: 'MCAT Flashcards', testId: 'footer-mcat-flashcards-link' },
        { href: '/es/enarm-ia', label: 'ENARM (México)', testId: 'footer-enarm-link' },
        { href: '/es/mir-ia', label: 'MIR (España)', testId: 'footer-mir-link' },
        { href: '/es/eunacom-ia', label: 'EUNACOM (Chile)', testId: 'footer-eunacom-link' },
      ];
  const studyToolsLinks = isES
    ? [
        { href: '/es/pdf-a-flashcards', label: 'PDF a Flashcards', testId: 'footer-pdf-flashcards-link' },
        { href: '/es/generador-quiz-ia', label: 'Generador de Quiz con IA', testId: 'footer-quiz-generator-link' },
        { href: '/es/generador-preguntas-opcion-multiple', label: 'Generador de Preguntas', testId: 'footer-mcq-generator-link' },
      ]
    : [
        { href: '/pdf-to-flashcards', label: 'PDF to Flashcards', testId: 'footer-pdf-flashcards-link' },
        { href: '/ai-quiz-generator', label: 'AI Quiz Generator', testId: 'footer-quiz-generator-link' },
        { href: '/multiple-choice-question-generator', label: 'MCQ Generator', testId: 'footer-mcq-generator-link' },
      ];
  return (
      <>
      {/* ─── FOOTER: el pie de la pieza publica (las cuatro columnas, el año y los dos botones de idioma) ─── */}
      <div className="lp lv-pieza">
      <footer className="p-foot">
        <div className="p-foot-in">
          <div className="p-foot-g">

            <div>
              <h3>{footerLabels.compare}</h3>
              <ul>
                <li><a href={`${vsBase}/anki`} data-testid="footer-vs-anki-link">vs Anki</a></li>
                <li><a href={`${vsBase}/quizlet`} data-testid="footer-vs-quizlet-link">vs Quizlet</a></li>
                <li><a href={`${vsBase}/notebooklm`} data-testid="footer-vs-notebooklm-link">vs NotebookLM</a></li>
                <li><a href={`${vsBase}/uworld`} data-testid="footer-vs-uworld-link">vs UWorld</a></li>
                <li><a href={`${vsBase}/studyfetch`} data-testid="footer-vs-studyfetch-link">vs StudyFetch</a></li>
              </ul>
            </div>

            <div>
              <h3>{footerLabels.medical}</h3>
              <ul>
                {medicalLinks.map((l) => (
                  <li key={l.href}><a href={l.href} data-testid={l.testId}>{l.label}</a></li>
                ))}
              </ul>
            </div>

            <div>
              <h3>{footerLabels.tools}</h3>
              <ul>
                {studyToolsLinks.map((l) => (
                  <li key={l.href}><a href={l.href} data-testid={l.testId}>{l.label}</a></li>
                ))}
              </ul>
            </div>

            <div>
              <h3>{footerLabels.company}</h3>
              <ul>
                {/* post-t7-2a (diseno 4.3): la liga a las paginas de profesores va PRIMERA,
                    con rotulo fijo por idioma (como Precios y Contacto, no es llave de
                    local). Esas paginas son estaticas de la WEB: en la app no se pinta,
                    con la misma compuerta de RUNTIME que el Blog de abajo. */}
                {!IS_APP_BUILD && (
                <li><a href={professorsHref} data-testid="footer-professors-link">{footerLabels.professors}</a></li>
                )}
                <li><a href={pricingHref} data-testid="footer-pricing-link">{footerLabels.pricing}</a></li>
                {/* #438 - EL BLOG ES DE LA WEB, NO DE LA APP. `quizessa.com/blog`
                    no existe dentro del contenedor de Capacitor: un toque aqui caeria
                    en el comodin del router y rebotaria al panel, que es peor que no
                    ofrecerlo. Hoy la app no monta esta pantalla (la ruta `/` rebota a
                    `/dashboard`, App.js), pero eso es una guarda de OTRO archivo: esta
                    es la del enlace, y es la que sobrevive si manana la landing se
                    montara en algun sitio de la app. Compuerta de RUNTIME: el enlace
                    viaja en los dos bundles y en la app no se pinta. */}
                {!IS_APP_BUILD && (
                <li><a href={blogHref} data-testid="footer-blog-link">{t('landing:footer.blog')}</a></li>
                )}
                <li><a href="mailto:support@quizessa.com" data-testid="footer-email-link">{footerLabels.contact}</a></li>
                <li><a href={legalLinks.privacy} data-testid="footer-privacy-link">{t('landing:footer.privacy')}</a></li>
                <li><a href={legalLinks.terms} data-testid="footer-terms-link">{t('landing:footer.terms')}</a></li>
              </ul>
            </div>

          </div>

          <div className="p-foot-b">
            <div>{t('common:footer.copyright', { year: new Date().getFullYear() })}</div>
            <div className="p-foot-l">
              <button type="button" onClick={() => switchLang('en')} aria-current={!isES ? 'true' : undefined} disabled={!isES}
                className={!isES ? 'on' : undefined} data-testid="footer-lang-en">EN</button>
              <button type="button" onClick={() => switchLang('es')} aria-current={isES ? 'true' : undefined} disabled={isES}
                className={isES ? 'on' : undefined} data-testid="footer-lang-es">ES</button>
            </div>
          </div>
        </div>
      </footer>
      </div>
      </>
  );
}

// La pagina publica de precios con la barra y el pie de la portada, SOLO en la WEB. En la app
// (IS_APP_BUILD) devuelve su contenido tal cual, sin barra ni pie: byte a byte la de antes.
// El contenido queda FUERA de la pieza (`.lp` le cambiaria letra, colores de ligas y fondo).
// La barra y el pie van cada uno en su caja de la pieza, dentro de una caja con la letra de la
// portada (lv-root), para que hereden lo mismo que en la portada. Entre la barra (fija) y el
// contenido va un hueco del alto de la barra, en CSS (lv-hueco-barra, index.css): ya vale en el
// pre-pintado y no brinca al montar. El fondo de la pagina entera es el de precios (lv-precios).
export function ConBarraYPie({ t, idioma, children }) {
  if (IS_APP_BUILD) return children;
  return <PreciosConBarraYPie t={t} idioma={idioma}>{children}</PreciosConBarraYPie>;
}

function PreciosConBarraYPie({ t, idioma, children }) {
  const { user } = useAuth();
  const switchLang = useIdiomaConGemela();
  const { navScrolled, mobileMenuOpen, setMobileMenuOpen } = useEstadoDeLaBarra();
  const isLoggedIn = user && !user.is_guest;
  return (
    <div className="lv-precios">
      <div className="lv-root text-[#f5f5f5]">
        <BarraPublica t={t} idioma={idioma} isLoggedIn={isLoggedIn} alCambiarIdioma={switchLang}
          navScrolled={navScrolled} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      </div>
      <div className="lv-hueco-barra" aria-hidden="true" />
      {children}
      <div className="lv-root text-[#f5f5f5]">
        <PiePublico t={t} idioma={idioma} alCambiarIdioma={switchLang} />
      </div>
    </div>
  );
}

// marca-3 (C.2 b, #736): la tira del aviso "StudyBuddy ahora es Quizessa" (ANEXO 1.1), justo debajo de la barra fija
// y a todo lo ancho. Es absoluta (index.css): se va con la pagina al bajar y no empuja nada, porque cae en el aire de
// arriba del titular. Su "x" la cierra para siempre en ese aparato (lib/avisoMarca.js); despues de AVISO_MARCA_HASTA
// ya no sale.
function TiraAvisoMarca({ t }) {
  const { mostrar, cerrar } = useAvisoMarca();
  if (!mostrar) return null;
  return (
    <div className="lv-aviso-marca" data-testid="aviso-marca-portada">
      <div className="lv-aviso-marca-in">
        <p role="status" data-testid="aviso-marca-portada-texto">{t(AVISO_MARCA_TEXTO)}</p>
        <button type="button" onClick={cerrar} aria-label={t(AVISO_MARCA_CERRAR)} data-testid="aviso-marca-portada-cerrar">
          <XIcon className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const { t, ready, LoadingGate, i18n } = useReadyTranslation(['landing', 'common']);
  const navigate = useNavigate();
  const { user, loginAsGuest } = useAuth();
  const [guestLoading, setGuestLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  // La barra (bajada y menu) y el cambio de idioma con gemela: los de la pieza publica, arriba.
  const { navScrolled, mobileMenuOpen, setMobileMenuOpen } = useEstadoDeLaBarra();
  const switchLang = useIdiomaConGemela();
  const heroRef = useRef(null);
  const hasAnimatedRef = useRef(false);
  const raizRef = useRef(null);

  const isLoggedIn = user && !user.is_guest;
  const mueve = sePuedeMover();
  const langKey = i18n?.language?.startsWith('es') ? 'es' : 'en';

  const handleGuest = async () => {
    if (user) { navigate('/dashboard'); return; }
    setGuestLoading(true);
    try {
      await loginAsGuest();
      toast.success(t('landing:toasts.guest_welcome'));
      navigate('/dashboard');
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) toast.error(t('common:toasts.guest_rate_limit'));
      else toast.error(t('common:toasts.generic_error'));
    } finally { setGuestLoading(false); }
  };

  const scrollTo = (id) => {
    setMobileMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // GSAP hero word-by-word entrance (once); later runs snap to final state.
  const heroTitle1 = t('landing:hero.title_part_1');
  const heroTitle2 = t('landing:hero.title_part_2');
  useEffect(() => {
    if (!ready) return;
    let attempts = 0;
    let retryTimer = null;
    const tryGsap = () => {
      if (window.gsap && heroRef.current) {
        const words = heroRef.current.querySelectorAll('.hero-word');
        if (hasAnimatedRef.current) {
          window.gsap.set(words, { opacity: 1, y: 0 });
        } else {
          window.gsap.to(words, { opacity: 1, y: 0, duration: 0.5, stagger: 0.07, ease: 'power3.out', delay: 0.2 });
          hasAnimatedRef.current = true;
        }
      } else if (attempts < 20) {
        attempts++;
        retryTimer = setTimeout(tryGsap, 150);
      } else if (heroRef.current) {
        heroRef.current.querySelectorAll('.hero-word').forEach((w) => w.classList.add('animated'));
        hasAnimatedRef.current = true;
      }
    };
    tryGsap();
    return () => { if (retryTimer) clearTimeout(retryTimer); };
  }, [ready, heroTitle1, heroTitle2]);

  // IntersectionObserver scroll-reveal
  useEffect(() => {
    if (!ready) return;
    const sections = document.querySelectorAll('.scroll-reveal');
    if (!sections.length) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('revealed'); observer.unobserve(entry.target); }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [ready]);

  // post-t7-2a (bloque E): los dibujos en vivo, la luz del borde de las tarjetas, la del fondo
  // y el aparecer al bajar (lib/escenasPortada.js). Cada dibujo arranca cuando EL aparece y se
  // detiene al salir; con "reducir movimiento" o en el pre-pintado no se monta nada.
  // Al cambiar de idioma se desmonta y se vuelve a montar: cada guion empieza por su estado inicial.
  useEffect(() => {
    if (!ready || !mueve || !raizRef.current) return undefined;
    return montarEscenas(raizRef.current);
  }, [ready, mueve, langKey]);

  if (!ready) return <LoadingGate />;

  // ---- i18n-driven derived data ----
  const heroLine1Words = t('landing:hero.title_part_1').split(' ').filter(Boolean);
  const heroLine2 = splitTransLine(t('landing:hero.title_part_2'));
  const heroLine2PrefixWords = heroLine2.prefix.split(' ').filter(Boolean);

  const arr = (v) => (Array.isArray(v) ? v : []);
  const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});

  const heroCard = obj(t('landing:hero.card', { returnObjects: true }));
  const pains = arr(t('landing:problem.pains', { returnObjects: true }));
  const howSteps = arr(t('landing:how_it_works.steps', { returnObjects: true }));
  const capabilities = arr(t('landing:capabilities.cards', { returnObjects: true }));
  const analyticsTopics = arr(t('landing:analytics.topics', { returnObjects: true }));
  const diffRows = arr(t('landing:differentiation.rows', { returnObjects: true }));
  const useCases = arr(t('landing:use_cases.items', { returnObjects: true }));
  const freePlan = obj(t('landing:pricing.free', { returnObjects: true }));
  const proPlan = obj(t('landing:pricing.pro', { returnObjects: true }));
  const faqItems = arr(t('landing:faq.items', { returnObjects: true }));
  const readinessValue = Number(t('landing:analytics.readiness_value')) || 78;

  const primaryLabel = guestLoading ? t('landing:hero.cta_loading') : t('landing:hero.cta_primary');

  // Auth-aware primary action button (hero / mid / final CTA share the same handler)
  const PrimaryCTA = ({ testidDashboard, testidStart, size = 'lg' }) => {
    // post-t7-2a (H): abajo de 360 el boton grande lleva 20 de relleno por lado y no 40. A 320 la
    // columna mide 272 y el texto mas largo con su flecha, 226: con 40 por lado el boton media 306,
    // ensanchaba la columna y empujaba el texto del arranque a 7 px del borde (auditoria de anchos).
    const sz = size === 'lg' ? 'px-5 min-[360px]:px-10 h-[52px] text-base' : 'px-6 h-10 text-sm';
    if (isLoggedIn) {
      return (
        <Link to="/dashboard">
          <Button className={`bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-full ${sz} font-semibold shadow-[0_8px_30px_rgba(255,107,0,0.28)] hover:-translate-y-px transition-transform`} data-testid={testidDashboard}>
            {t('landing:hero.cta_authenticated')} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      );
    }
    return (
      <Button onClick={handleGuest} disabled={guestLoading}
        className={`bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-full ${sz} font-semibold shadow-[0_8px_30px_rgba(255,107,0,0.28)] hover:-translate-y-px transition-transform`} data-testid={testidStart}>
        {guestLoading ? t('landing:hero.cta_loading') : <>{t('landing:hero.cta_primary')} <ArrowRight className="w-4 h-4 ml-2" /></>}
      </Button>
    );
  };

  return (
    <div ref={raizRef} className="lv-root lv-portada min-h-screen bg-[#111111] text-[#f5f5f5] overflow-x-hidden">

      {/* post-t7-2a (E): la luz del fondo que sigue al cursor (solo con mouse; la monta lib/escenasPortada.js) */}
      <div className="lv-gl" aria-hidden="true" />

      {/* header top glow */}
      <div className="lv-top-glow" aria-hidden="true" />

      <BarraPublica t={t} idioma={i18n?.language} isLoggedIn={isLoggedIn} alCambiarIdioma={switchLang}
        navScrolled={navScrolled} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} irA={scrollTo} />

      {/* marca-3 (#736): la tira del aviso del nombre nuevo, justo debajo de la barra (index.css, .lv-aviso-marca). */}
      <TiraAvisoMarca t={t} />

      {/* ─── HERO ─── */}
      <section className="min-h-[92vh] flex items-center px-6 md:px-12 pt-32 pb-10 relative" data-testid="hero-section">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] bg-[radial-gradient(circle,rgba(255,79,0,0.09)_0%,transparent_70%)] pointer-events-none" />
        <div className="w-full max-w-[1200px] mx-auto z-10 flex flex-col lg:flex-row items-center gap-11 lg:gap-16">
          {/* copy */}
          <div className="flex-1 text-center lg:text-left" ref={heroRef}>
            <h1 className="font-bold leading-[1.06] mb-7 font-['Space_Grotesk']" data-testid="hero-title"
              style={{ fontSize: 'clamp(38px, 5.4vw, 66px)', letterSpacing: '-0.035em' }}>
              {/* post-t7-2a (diseno 3.3): cada palabra lleva su espacio DE VERDAD dentro de su caja.
                  Al final del renglon de esa caja el espacio no ocupa ancho, asi que cada renglon
                  mide lo mismo que antes; pero el texto, sin etiquetas, ya se lee con sus espacios
                  ("Know exactly what to study next."), tambien entre renglon y renglon. */}
              {heroLine1Words.map((word, i) => (
                <span key={`l1-${i}`} className="hero-word" style={{ marginRight: '0.3em' }}>{word}{' '}</span>
              ))}
              <br />
              {heroLine2PrefixWords.map((word, i) => (
                <span key={`l2-${i}`} className="hero-word" style={{ marginRight: '0.3em' }}>{word}{' '}</span>
              ))}
              {heroLine2.em && (<em className="not-italic text-[#FF6B00] hero-word">{heroLine2.em}</em>)}
            </h1>
            <p className="text-base sm:text-lg text-[#8c8c8c] max-w-[560px] mx-auto lg:mx-0 leading-relaxed mb-9">
              {t('landing:hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center lg:justify-start justify-center gap-3.5">
              <PrimaryCTA testidDashboard="hero-dashboard-btn" testidStart="hero-start-btn" />
              <button onClick={() => scrollTo('how-it-works')}
                className="inline-flex items-center gap-2 text-[#8c8c8c] hover:text-white font-medium h-[52px] px-2 text-base transition-colors" data-testid="hero-scroll-link">
                {t('landing:hero.cta_secondary')} <ArrowDown className="w-4 h-4" />
              </button>
            </div>
            {!isLoggedIn && <p className="text-[13px] text-[#757575] mt-[22px]">{t('landing:hero.cta_microcopy')}</p>}
            {/* TANDA 27 (#242/#243) — la puerta a la app, debajo del CTA.
                Va DESPUÉS del microcopy y no entre él y el botón: ese renglón es
                la letra chica DE ESE botón ("gratis, sin tarjeta") y separarlo de
                su CTA lo dejaría huérfano. Éste es el último bloque de la columna,
                que es donde un anuncio discreto no le compite a nada. */}
            <AppStoreBloque variante="landing" />
          </div>
          {/* animated card */}
          <div className="flex-1 w-full max-w-[460px] lg:max-w-none">
            <div className="lp lv-caja"><TarjetaDeArriba card={heroCard} /></div>
            <p className="text-[13px] text-[#8c8c8c] mt-4 text-center">{t('landing:hero.caption')}</p>
          </div>
        </div>
      </section>

      {/* ─── DEL PROBLEMA A LOS PRECIOS (post-t7-2a, bloque E) ─── */}
      {/* Las secciones de la maqueta de la portada, con la piel y los dibujos de la pieza publica,
          dentro de UNA caja .lp (src/styles/portada.css). Cada dibujo trae en el HTML su estado
          final con todo su texto; lib/escenasPortada.js los mueve cuando se puede. Con movimiento,
          la marca js-rv esconde cada tarjeta hasta que aparece al bajar. */}
      <div className={`lp lv-caja${mueve ? ' js-rv' : ''}`}>

        {/* ─── PROBLEM ─── */}
        <section className="p-sec p-first" data-testid="problem-section">
          <div className="wrap p-prob">
            <div className="rv">
              <h2>{t('landing:problem.title_part_1')} <em>{t('landing:problem.title_em')}</em></h2>
              <p className="p-lead">{t('landing:problem.lead')}</p>
            </div>
            <div className="pc rv p-pains" data-once="dolor">
              {pains.map((p, i) => (
                <div key={i} className="lv-pain"><span className="lv-x"><PainXGlyph /></span><span>{p}</span></div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── HOW IT WORKS ─── */}
        <section id="how-it-works" className="p-sec" data-testid="how-it-works-section">
          <div className="wrap">
            <p className="p-eyebrow">{t('landing:how_it_works.eyebrow')}</p>
            <h2>{t('landing:how_it_works.title_part_1')} <em>{t('landing:how_it_works.title_em')}</em></h2>
            <ol className="p-steps">
              <li className="pc rv" data-esc="como1">
                <div className="viz hv">
                  <div className="up-z">
                    <span className="up-i"><SubirGlyph /></span>
                    <div className="up-f">
                      <span className="up-t">PDF</span>
                      <span className="up-n">{t('landing:examples.file')}</span>
                      <span className="up-ok"><PalomaGlyph /></span>
                    </div>
                    <div className="up-bar"><i /></div>
                  </div>
                  <div className="up-ty" aria-hidden="true"><span className="on">PDF</span><span>PPTX</span><span>DOCX</span><span>TXT</span></div>
                </div>
                <PasoDeComo paso={howSteps[0]} />
              </li>
              <li className="pc rv" data-esc="como2">
                <div className="viz hv">
                  <div className="gen">
                    <div className="gen-c"><b data-n="q" data-v={CUENTAS.preguntas}>{CUENTAS.preguntas}</b><span>{t('landing:how_it_works.viz.questions')}</span></div>
                    <div className="gen-c"><b data-n="f" data-v={CUENTAS.flashcards}>{CUENTAS.flashcards}</b><span>{t('landing:how_it_works.viz.flashcards')}</span></div>
                  </div>
                  <ul className="gen-l">
                    {['l1', 'l2', 'l3'].map((k, i) => (
                      <li key={k}><b>{i + 1}</b><span>{t(`landing:examples.generated.${k}`)}</span></li>
                    ))}
                  </ul>
                </div>
                <PasoDeComo paso={howSteps[1]} />
              </li>
              <li className="pc rv" data-esc="como3">
                <div className="viz hv">
                  <div className="ses-h">
                    <span className="vk">{t('landing:analytics.readiness_label')}</span>
                    <span className="ses-n"><b>{SESIONES[SESIONES.length - 1]}</b><small>%</small></span>
                  </div>
                  <div className="ses-b">
                    {SESIONES.map((v, i) => (
                      <div key={i} className="ses-c">
                        <div><i data-v={v} style={{ height: `${v}%` }} /></div>
                        <span>{t('landing:how_it_works.viz.session')} {i + 1}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <PasoDeComo paso={howSteps[2]} />
              </li>
            </ol>
            <div className="p-mid" data-testid="mid-page-cta">
              {isLoggedIn ? (
                <Link to="/dashboard" className="p-cta" data-testid="mid-cta-dashboard-btn">
                  {t('landing:hero.cta_authenticated')}<FlechaGlyph />
                </Link>
              ) : (
                <button type="button" onClick={handleGuest} disabled={guestLoading} className="p-cta" data-testid="mid-cta-start-btn">
                  {guestLoading ? t('landing:hero.cta_loading') : <>{t('landing:hero.cta_primary')}<FlechaGlyph /></>}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* ─── ADAPTIVE STUDY ─── */}
        {/* Las cuatro tarjetas de la pagina de profesores, dichas de tu. Ningun dibujo dice numeros del motor. */}
        <section id="adaptive" className="p-sec" data-testid="adaptive-section">
          <div className="wrap">
            <div className="rv">
              <p className="p-eyebrow">{t('landing:adaptive.eyebrow')}</p>
              <h2>{t('landing:adaptive.title_part_1')} <em>{t('landing:adaptive.title_em')}</em></h2>
              <p className="p-lead">{t('landing:adaptive.lead')}</p>
            </div>
            <div className="bento">
              <article className="pc rv s5">
                <div className="viz" data-esc="a1">
                  <p className="vk">{t('landing:adaptive.tiles.often.label')}</p>
                  {FRECUENCIAS.map(([sq, k, ancho]) => (
                    <div key={k} className="fr">
                      <div className="fr-l"><i className={`sq ${sq}`} /><span>{t(`landing:adaptive.tiles.often.${k}`)}</span></div>
                      <div className="fr-t"><i style={{ width: `${ancho}%` }} /></div>
                    </div>
                  ))}
                </div>
                <TextoDeTarjeta titulo={t('landing:adaptive.tiles.often.title')} texto={t('landing:adaptive.tiles.often.desc')} />
              </article>
              <article className="pc rv s7">
                <div className="viz" data-esc="a2">
                  <p className="vk">{t('landing:adaptive.tiles.fading.label')}</p>
                  <div>
                    <svg className="crv" viewBox="0 0 320 108" aria-hidden="true" data-x0={CURVA.x0} data-w={CURVA.w} data-pts={CURVA.pts}>
                      <defs>
                        <linearGradient id="cvg" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0" stopColor="#FF6B00" stopOpacity=".3" />
                          <stop offset="1" stopColor="#FF6B00" stopOpacity="0" />
                        </linearGradient>
                        <clipPath id="cvc"><rect className="crv-c" x="0" y="0" width="320" height="108" /></clipPath>
                      </defs>
                      <path className="crv-g" d={CURVA.rejilla} />
                      <path className="crv-b" d={CURVA.base} />
                      <g className="crv-run">
                        <g clipPath="url(#cvc)">
                          <path className="crv-a" d={CURVA.area} fill="url(#cvg)" />
                          <path className="crv-d" d={CURVA.sinRepaso} />
                          <path className="crv-o" d={CURVA.conRepaso} />
                        </g>
                        <g className="crv-p">
                          {CURVA.repasos.map((c, i) => <circle key={i} cx={c.cx} cy={c.cy} r={c.r} />)}
                        </g>
                        <circle className="crv-m" r={CURVA.marca.r} cx={CURVA.marca.cx} cy={CURVA.marca.cy} />
                      </g>
                    </svg>
                    <div className="crv-ax">
                      <span style={{ left: CURVA.dias[0] }}><span>{t('landing:adaptive.tiles.fading.day')}</span> 1</span>
                      <span style={{ left: CURVA.dias[1] }}>3</span>
                      <span style={{ left: CURVA.dias[2] }}>7</span>
                      <span className="fin" style={{ left: CURVA.dias[3] }}>14</span>
                    </div>
                  </div>
                  <div className="lgd">
                    <span><i className="d" /><span>{t('landing:adaptive.tiles.fading.no_practice')}</span></span>
                    <span><i /><span>{t('landing:adaptive.tiles.fading.spaced')}</span></span>
                  </div>
                  <p className="vn">{t('landing:adaptive.tiles.fading.note')}</p>
                </div>
                <TextoDeTarjeta titulo={t('landing:adaptive.tiles.fading.title')} texto={t('landing:adaptive.tiles.fading.desc')} />
              </article>
              <article className="pc rv s7">
                <div className="viz" data-esc="a3">
                  <div className="vk-r">
                    <p className="vk">{t('landing:adaptive.tiles.difficulty.label')}</p>
                    <span className="tp-c tm-a"><span>{t('landing:examples.topics.annuities')}</span></span>
                  </div>
                  <ol className="sx">
                    {SEGUIDAS.map((c, i) => (
                      <li key={i} className={c.r} style={{ '--n': c.n }} data-n={c.n}>
                        <b>{i + 1}</b>
                        <span className="sx-c">
                          <span className={`db ${c.d}`}><DificultadGlyph /><span>{t(`landing:adaptive.tiles.difficulty.${INSIGNIA[c.d]}`)}</span></span>
                          <i className="sx-r" aria-hidden="true" dangerouslySetInnerHTML={{ __html: c.r === 'ok' ? PALOMA : (c.r === 'ko' ? TACHE : '') }} />
                        </span>
                      </li>
                    ))}
                  </ol>
                  <ul className="st-r">
                    <li className="sube"><span className="st-i" aria-hidden="true"><SubeGlyph /></span><span>{t('landing:adaptive.tiles.difficulty.up')}</span></li>
                    <li className="baja"><span className="st-i" aria-hidden="true"><BajaGlyph /></span><span>{t('landing:adaptive.tiles.difficulty.down')}</span></li>
                  </ul>
                </div>
                <TextoDeTarjeta titulo={t('landing:adaptive.tiles.difficulty.title')} texto={t('landing:adaptive.tiles.difficulty.desc')} />
              </article>
              <article className="pc rv s5">
                <div className="viz" data-esc="a4">
                  <p className="vk">{t('landing:adaptive.tiles.mix.label')}</p>
                  <ol className="mz">
                    {MEZCLA.map((f, i) => (
                      <li key={i} className={`e-${f.e}`} data-t={f.t}>
                        <b>{i + 1}</b>
                        <div className="mz-c">
                          <span className="mz-t">{t(`landing:examples.topics.${TEMA[f.t]}`)}</span>
                          <span className="mz-w"><i className="mz-e">{t(`landing:adaptive.tiles.mix.${ESTADO[f.e]}`)}</i><span>{t(`landing:adaptive.tiles.mix.${f.r}`)}</span></span>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
                <TextoDeTarjeta titulo={t('landing:adaptive.tiles.mix.title')} texto={t('landing:adaptive.tiles.mix.desc')} />
              </article>
            </div>
          </div>
        </section>

        {/* ─── CAPABILITIES ─── */}
        {/* Las cuatro tarjetas cambian su icono por un dibujo en vivo con texto de verdad, todo del mismo documento. */}
        <section id="features" className="p-sec" data-testid="capabilities-section">
          <div className="wrap">
            <p className="p-eyebrow">{t('landing:capabilities.eyebrow')}</p>
            <h2 className="rv">{t('landing:capabilities.title_line_1')}<br className="br-sm" /> {t('landing:capabilities.title_line_2')}</h2>
            <div className="bento">
              <article className="pc rv s7">
                <div className="viz cz" data-esc="c1">
                  <div className="mq">
                    <p className="mq-q">{t('landing:examples.q1.question')}</p>
                    <div className="mq-o no" data-r="no"><b>B</b><ConFormula texto={t('landing:examples.q1.wrong')} /><span className="mq-k"><TacheGlyph /></span></div>
                    <div className="mq-o si" data-r="si"><b>C</b><ConFormula texto={t('landing:examples.q1.right')} /><span className="mq-k"><PalomaGlyph /></span></div>
                    <div className="mq-e">
                      <span><TacheGlyph /><span>{t('landing:hero.card.feedback_label')}</span></span>
                      <p>{t('landing:examples.q1.why')}</p>
                    </div>
                  </div>
                </div>
                <TextoDeTarjeta titulo={capabilities[0]?.title} texto={capabilities[0]?.desc} />
              </article>
              <article className="pc rv s5">
                <div className="viz cz" data-esc="c2">
                  <div className="fc" data-k="1">
                    <div className="fc-s s1" /><div className="fc-s s2" />
                    <div className="fc-w">
                      <div className="fc-card">
                        <div className="fc-f">
                          <span className="fc-q" aria-hidden="true">?</span>
                          {['f1', 'f2', 'f3'].map((k, i) => <p key={k} className={`fk k${i + 1}`}>{t(`landing:examples.flashcards.${k}.q`)}</p>)}
                        </div>
                        <div className="fc-b">
                          {['f1', 'f2', 'f3'].map((k, i) => <p key={k} className={`fk k${i + 1}`}>{t(`landing:examples.flashcards.${k}.a`)}</p>)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <span className="fc-n" aria-hidden="true"><b>1</b> / {CUENTAS.flashcards}</span>
                </div>
                <TextoDeTarjeta titulo={capabilities[1]?.title} texto={capabilities[1]?.desc} />
              </article>
              <article className="pc rv s5">
                <div className="viz cz" data-esc="c3">
                  <div className="nx">
                    <div className="nx-c">
                      <span className="nx-l">{t('landing:capabilities.viz.topic')}</span>
                      <span className="nx-v"><i className="nx-p tm-a" /><span>{t('landing:examples.topics.annuities')}</span></span>
                    </div>
                    <div className="nx-c">
                      <span className="nx-l">{t('landing:capabilities.viz.question')}</span>
                      <span className="nx-v">{t('landing:capabilities.viz.unseen')}</span>
                    </div>
                    <div className="nx-c">
                      <span className="nx-l">{t('landing:capabilities.viz.difficulty')}</span>
                      <span className="nx-v"><span className="db m"><TargetGlyph /><span>{t('landing:adaptive.tiles.difficulty.medium')}</span></span></span>
                    </div>
                  </div>
                  <div className="nx-q"><b aria-hidden="true">?</b><span>{t('landing:examples.q2')}</span><FlechaGlyph /></div>
                </div>
                <TextoDeTarjeta titulo={capabilities[2]?.title} texto={capabilities[2]?.desc} />
              </article>
              <article className="pc rv s7">
                <div className="viz cz" data-esc="c4">
                  <div className="tp">
                    {analyticsTopics.slice(0, 3).map((tp, i) => {
                      const v = Number(tp.value) || 0;
                      return (
                        <div key={i} className="tp-r">
                          <span>{tp.name}</span><b>{v}%</b>
                          <div className="tp-t"><i data-v={v} style={{ width: `${v}%` }} /></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <TextoDeTarjeta titulo={capabilities[3]?.title} texto={capabilities[3]?.desc} />
              </article>
            </div>
          </div>
        </section>

        {/* ─── ANALYTICS ─── */}
        {/* El puntaje cuenta y las barras se llenan UNA vez, al aparecer (escena "ana"). */}
        <section id="analytics" className="p-sec" data-testid="analytics-section">
          <div className="wrap">
            <p className="p-eyebrow">{t('landing:analytics.eyebrow')}</p>
            <h2 className="rv">{t('landing:analytics.title_part_1')} <em>{t('landing:analytics.title_em')}</em></h2>
            <p className="p-lead p-lead-b">{t('landing:analytics.lead')}</p>
            <div className="rv" data-once="ana">
              <div className="lv-readiness-card" data-testid="landing-readiness-card">
                <div className="lv-readiness-head">
                  <div>
                    <div className="lv-rt-label">{t('landing:analytics.readiness_label')}</div>
                    <div className="lv-readiness-num"><span className="ana-n" data-v={readinessValue} data-testid="landing-readiness-num">{readinessValue}</span><span className="lv-pct">%</span></div>
                  </div>
                  <span className="lv-readiness-badge">{t('landing:analytics.readiness_badge')}</span>
                </div>
                <div className="lv-readiness-bar"><div className="lv-readiness-bar-fill" data-v={readinessValue} style={{ width: `${readinessValue}%` }} /></div>
                <div className="lv-readiness-foot">{t('landing:analytics.readiness_foot')}</div>
              </div>
              <div className="ana-h"><BarChart3 size={18} /><h3>{t('landing:analytics.topics_heading')}</h3></div>
              <div className="ana-l" data-testid="landing-topics-list">
                {analyticsTopics.map((tp, i) => (
                  <div key={i} className="lv-topic-card pc">
                    <div className="lv-tc-name">{tp.name}</div>
                    <div className="lv-tc-meta">{tp.meta}</div>
                    <div className="lv-tc-label">{tp.mastery_label}</div>
                    <div className="lv-tc-track"><div className="lv-tc-fill" data-v={tp.value} style={{ width: `${tp.value}%` }} /></div>
                    <div className="lv-tc-seen">{tp.seen}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ─── DIFFERENTIATION ─── */}
        {/* Cada renglon entra cuando aparece y el de StudyBuddy se enciende medio segundo despues (escena "cmp"). */}
        <section id="why" className="p-sec" data-testid="differentiation-section">
          <div className="wrap">
            <p className="p-eyebrow">{t('landing:differentiation.eyebrow')}</p>
            <h2 className="rv">{t('landing:differentiation.title_line_1')}<br className="br-sm" /> {t('landing:differentiation.title_line_2')}</h2>
            <div className="lv-compare pc rv" data-once="cmp">
              {diffRows.map((row, i) => (
                <div key={i} className={`lv-crow${row.highlight ? ' hl' : ''}`}>
                  <div className="lv-cname">
                    {row.name === 'Quizessa'
                      ? <span>Quiz<span className="text-[#FF6B00]">essa</span></span>
                      : row.name}
                  </div>
                  <div className="lv-cdesc">{row.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── USE CASES ─── */}
        <section id="use-cases" className="p-sec" data-testid="use-cases-section">
          <div className="wrap">
            <p className="p-eyebrow">{t('landing:use_cases.eyebrow')}</p>
            <h2 className="rv">{t('landing:use_cases.title_line_1')}<br className="br-sm" /> {t('landing:use_cases.title_line_2')}</h2>
            <div className="p-ucs">
              {UC_VISUALS.map((visual, i) => {
                const uc = useCases[i] || {};
                const Icon = visual.icon;
                return (
                  <article key={i} className="pc rv p-uc">
                    <span className={`p-ic ${visual.tono}`}><Icon size={20} /></span>
                    <h3>{uc.title}</h3>
                    <p>{uc.desc}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* ─── PRICING ─── */}
        {/* Solo la piel nueva de tarjeta: ningun texto, precio, boton ni destino cambia. */}
        <section id="pricing" className="p-sec" data-testid="pricing-section">
          <div className="wrap">
            <div className="p-ctr rv">
              <p className="p-eyebrow">{t('landing:pricing.eyebrow')}</p>
              <h2>{t('landing:pricing.title')}</h2>
              <p className="p-lead">{t('landing:pricing.lead')}</p>
            </div>
            <div className="p-plans">
              {/* Free */}
              <div className="pc rv p-plan">
                <div className="p-pn">{freePlan.name}</div>
                <div className="p-pp">{freePlan.price}<small> {freePlan.period}</small></div>
                <div className="p-pt">{freePlan.note}</div>
                <ul>
                  {arr(freePlan.features).map((f, i) => (
                    <li key={i}><PalomaGlyph /><span>{typeof f === 'string' && f.startsWith('Sabi') ? <><span style={{ color: '#fff', fontWeight: 700 }}>Sabi</span>{f.slice(4)}</> : f}</span></li>
                  ))}
                </ul>
                {isLoggedIn ? (
                  <Link to="/dashboard" className="p-pb" data-testid="pricing-free-cta">
                    {t('landing:pricing.cta_authenticated')}
                  </Link>
                ) : (
                  <button type="button" onClick={handleGuest} disabled={guestLoading} className="p-pb" data-testid="pricing-free-cta">
                    {guestLoading ? t('landing:pricing.cta_loading') : freePlan.cta}
                  </button>
                )}
              </div>
              {/* Pro */}
              <div className="pc rv p-plan pro">
                <span className="p-pop">{t('landing:pricing.popular_badge')}</span>
                <div className="p-pn">{proPlan.name}</div>
                <div className="p-pp">{proPlan.price}<small> {proPlan.period}</small></div>
                <div className="p-pt">{proPlan.note}</div>
                <ul>
                  {arr(proPlan.features).map((f, i) => (
                    <li key={i}><PalomaGlyph /><span>{typeof f === 'string' && f.startsWith('Sabi') ? <><span style={{ color: '#fff', fontWeight: 700 }}>Sabi</span>{f.slice(4)}</> : f}</span></li>
                  ))}
                </ul>
                <Link to="/signup" className="p-pb" data-testid="pricing-pro-cta">
                  {proPlan.cta}
                </Link>
              </div>
            </div>
            <p className="p-pf">{t('landing:pricing.foot')}</p>
          </div>
        </section>

      </div>

      {/* ─── FAQ ─── */}
      {/* Las preguntas frecuentes de la pieza publica: una abierta a la vez, y todas las
          respuestas en el HTML aunque esten cerradas (los robots las leen). */}
      <section id="faq" className="py-24 border-t border-[#262626] scroll-reveal" data-testid="faq-section">
        <div className="lp lv-pieza">
          <div className="wrap p-faq">
            <h2>{t('landing:faq.title')}</h2>
            <div className="p-fqs">
              {faqItems.map((faq, i) => (
                <div key={i} className={`p-fq${openFaq === i ? ' open' : ''}`} data-testid={`faq-item-${i}`}>
                  <button type="button" onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    aria-expanded={openFaq === i} data-testid={`faq-toggle-${i}`}>
                    <span>{faq.q}</span>
                    <ChevronDown aria-hidden="true" />
                  </button>
                  <div className="p-fa">
                    <p>{faq.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="py-28 px-6 text-center relative scroll-reveal" data-testid="cta-section">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] bg-[radial-gradient(circle,rgba(255,79,0,0.07)_0%,transparent_70%)] pointer-events-none" />
        {/* post-t7-2a (H3): el alto de renglon de 1.1 de la letra se repite en el ancho chico: el tamano de
            letra de ese ancho trae su propio renglon (40 px) y desde 768 dejaba 52 px de letra en 40 de
            renglon, con los dos renglones encimados. La letra no cambia. */}
        <h2 className="text-3xl sm:text-4xl sm:leading-[1.1] md:text-[52px] font-bold leading-[1.1] mb-4 font-['Space_Grotesk'] relative" style={{ letterSpacing: '-0.02em' }}>
          {t('landing:final_cta.title_part_1')} <em className="not-italic text-[#FF6B00]">{t('landing:final_cta.title_em')}</em>
        </h2>
        <p className="text-base md:text-[17px] text-[#8c8c8c] mb-9 max-w-[520px] mx-auto relative">{t('landing:final_cta.subtitle')}</p>
        <div className="flex flex-col items-center gap-3 relative">
          <PrimaryCTA testidDashboard="cta-dashboard-btn" testidStart="cta-start-btn" />
          {!isLoggedIn && <p className="text-xs text-[#757575]">{t('landing:final_cta.microcopy')}</p>}
        </div>
      </section>

      <PiePublico t={t} idioma={i18n?.language} alCambiarIdioma={switchLang} />
    </div>
  );
}
